/* istanbul ignore file */
import { useContext, useEffect, useState } from 'react';
import { DEFAULT_PAGINATION } from '../../../../ResourceAllocationOverview/JiraStats/useJiraStats/useJiraStats';
import ProjectProgressionContext from '../../state/context';
import { ProjectProgressionFiltersContext } from '../../../../state/filters/contexts';
import usePmSensorAuthId from 'components/hooks/PM/usePmSensorAuthId';
import usePagination from 'common/fetchHooks/usePagination';
import { getProgressionTable } from 'common/services/project-progression.service';
import useTableSortBy from 'common/fetchHooks/useTableSortBy';
import { RA_API_ERRORS } from 'common/services/resource-allocation.service';
import useInsightsSortBy from './useInsightsSortBy';
import { xor } from 'lodash';
import useSupportedCols from './useSupportedCols';
import useComponentLoading from '../../state/useComponentLoading';
import { PROJECT_PROGRESSION_COMPONENTS } from '../../state/components';
import useTrimmedKeyword from 'common/hooks/utils/useTrimmedKeyword';
import { useQuery } from '@tanstack/react-query';
import debounce from 'debounce-promise';
import useTableTypes from './useTableTypes';

const getProgressionTableDebounced = debounce(getProgressionTable, 500);

const useProgressionTable = () => {
  const {
    state: {
      isInitialized,
      period,
      filtersPayload,
      extraPayload,
      filtersBarHeight
    }
  } = useContext(ProjectProgressionFiltersContext);
  const {
    state: { selectedInsightKey }
  } = useContext(ProjectProgressionContext);

  const { tableTypes, isLoading: isLoadingTableTypes } = useTableTypes();
  const defaultTableType = tableTypes.ISSUES;
  const pmSensorAuthId = usePmSensorAuthId();

  const [isLoading, setIsLoading] = useComponentLoading(
    PROJECT_PROGRESSION_COMPONENTS.PROGRESSION_TABLE
  );
  const [data, setData] = useState([]);

  const [tableType, setTableType] = useState(defaultTableType);
  const [stateTypes, setStateTypes] = useState([]);
  const [keyword, setKeyword, keywordDependency] = useTrimmedKeyword();
  const pagination = usePagination(DEFAULT_PAGINATION.PAGE_SIZE);
  const tableSortBy = useTableSortBy(defaultTableType.sortBy.default);

  useSupportedCols({ tableSortBy, tableType });
  useInsightsSortBy({ tableSortBy, tableType });

  const onClickStateType = newType =>
    setStateTypes(prevValues => xor(prevValues, [newType]));
  const clearStateTypes = () => setStateTypes([]);

  const handleFetchErrors = e => {
    pagination.setTotal(0);
    setData([]);
    const desc = e?.response?.data?.description || e?.message || '';
    if (desc.includes(RA_API_ERRORS.UNSUPPORTED_SORT_BY)) {
      tableSortBy.onChange(tableType.sortBy.default);
    }
  };

  useQuery({
    retry: false,
    enabled: isInitialized && !isLoadingTableTypes,
    refetchOnWindowFocus: false,
    queryKey: [
      'progressionTable',
      pmSensorAuthId,
      period.before,
      period.after,
      tableType.type,
      tableType.issueType,
      stateTypes,
      pagination.qs.limit,
      pagination.qs.offset,
      keyword,
      selectedInsightKey,
      filtersPayload,
      tableSortBy.key,
      tableSortBy.dir
    ],
    queryFn: () => {
      setIsLoading(true);
      return getProgressionTableDebounced({
        sensor_authorization_id: pmSensorAuthId,
        before: period.before,
        after: period.after,
        type: tableType.type,
        issue_type: tableType.issueType,
        categories: stateTypes,
        limit: pagination.qs.limit,
        offset: pagination.qs.offset,
        keyword,
        insight_id: selectedInsightKey,
        filtersPayload,
        sort_by: tableSortBy.key,
        sort_dir: tableSortBy.dir,
        top_assignees: 4,
        ...extraPayload
      });
    },
    onSuccess: res => {
      pagination.setTotal(res?.total);
      setData(res);
    },
    onError: e => {
      handleFetchErrors(e);
    },
    onSettled: () => {
      setIsLoading(false);
    }
  });

  useEffect(() => {
    pagination.resetPages();
  }, [
    tableType,
    stateTypes,
    period.before,
    period.after,
    keywordDependency,
    tableSortBy.key,
    tableSortBy.dir
  ]);

  useEffect(() => {
    if (!!selectedInsightKey) {
      if (tableType.level > 0) {
        setTableType(defaultTableType);
      }
      clearStateTypes();
      setKeyword('');
    }
  }, [selectedInsightKey]);

  useEffect(() => {
    setKeyword('');
  }, [tableType]);

  return {
    isLoading,
    data,
    tableType,
    setTableType,
    stateTypes,
    onClickStateType,
    keyword,
    setKeyword,
    pagination,
    tableSortBy,
    filtersBarHeight
  };
};

export default useProgressionTable;
